import { create } from 'zustand';
import type { Message } from '../components/chat-message';
import { chatAssistantService, ConversationSummary } from '../services/chat-assistant-service';

interface ChatState {
  messages: Message[];
  conversationId: string | null;
  conversations: ConversationSummary[];
  isLoading: boolean;
  isLoadingConversations: boolean;

  sendMessage: (content: string, pageContext?: string) => Promise<void>;
  startNewConversation: () => void;
  loadConversation: (id: string) => Promise<void>;
  fetchConversations: () => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [],
  conversationId: null,
  conversations: [],
  isLoading: false,
  isLoadingConversations: false,

  sendMessage: async (content: string, pageContext?: string) => {
    const userMessage: Message = {
      role: 'user',
      content,
      timestamp: new Date(),
    };
    set((state) => ({
      messages: [...state.messages, userMessage],
      isLoading: true,
    }));

    try {
      const { response, toolResults, conversationId } = await chatAssistantService.sendMessage(
        content,
        get().messages.slice(0, -1), // history before this message
        pageContext,
        get().conversationId || undefined
      );

      const assistantMessage: Message = {
        role: 'assistant',
        content: response,
        timestamp: new Date(),
        toolResults: toolResults.length > 0 ? toolResults : undefined,
      };

      set((state) => ({
        messages: [...state.messages, assistantMessage],
        conversationId,
        isLoading: false,
      }));

      // Refresh conversations list in background
      get().fetchConversations();
    } catch (error: any) {
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Lo siento, ocurrió un error. Por favor intenta de nuevo.',
        timestamp: new Date(),
      };
      set((state) => ({
        messages: [...state.messages, errorMessage],
        isLoading: false,
      }));
      throw error;
    }
  },

  startNewConversation: () => {
    set({
      messages: [],
      conversationId: null,
    });
  },

  loadConversation: async (id: string) => {
    set({ isLoading: true });
    try {
      const conversation = await chatAssistantService.getConversation(id);

      const messages: Message[] = conversation.messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.createdAt),
        // Restore toolResults from pageContext if present
        toolResults: msg.pageContext?.toolResults || undefined,
      }));

      set({
        messages,
        conversationId: id,
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  fetchConversations: async () => {
    set({ isLoadingConversations: true });
    try {
      const conversations = await chatAssistantService.getConversations();
      set({ conversations, isLoadingConversations: false });
    } catch {
      set({ isLoadingConversations: false });
    }
  },

  deleteConversation: async (id: string) => {
    try {
      await chatAssistantService.deleteConversation(id);
      set((state) => ({
        conversations: state.conversations.filter((c) => c.id !== id),
        // If deleting current conversation, reset
        ...(state.conversationId === id ? { messages: [], conversationId: null } : {}),
      }));
    } catch {
      // silently fail
    }
  },
}));
